// ---------- Import ---------- //
import axios from "axios";
import Cookies from "universal-cookie";

let navigateFunction = null;

// Function to set navigate
export const setNavigateFunction = (navigate) => {
    navigateFunction = navigate;
};

// ---------- Const Variables ---------- //
 const baseURL = 'https://api.bigjump.app/api';
// const baseURL = "https://localhost:7017/api";

const cookie = new Cookies();

// ---------- URL Normalization ---------- //
/**
 * Recursively converts all http://api.bigjump.app URLs to https://
 * to prevent Mixed Content browser warnings.
 * This is a safety net for any HTTP URLs that may slip through from the backend.
 */
const normalizeUrlsToHttps = (data) => {
    if (typeof data === 'string') {
        // Replace http:// with https:// for api.bigjump.app URLs
        return data.replace(/http:\/\/api\.bigjump\.app/g, 'https://api.bigjump.app');
    }
    if (Array.isArray(data)) {
        return data.map(normalizeUrlsToHttps);
    }
    if (data && typeof data === 'object') {
        const normalized = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                normalized[key] = normalizeUrlsToHttps(data[key]);
            }
        }
        return normalized;
    }
    return data;
};

// ---------- Functions ---------- //
// Ensure tenant exists early
const ensureTenantInitialized = () => {
    let tenant = localStorage.getItem('tenant');
    if (!tenant) {
        // Fallback default tenant to avoid first-load errors
        // Previously hardcoded in App.js; keeping same value here
        // tenant = 'bj-21f4d8c6';
        tenant = 'tenant-2';
        localStorage.setItem('tenant', tenant);
    }
    return tenant;
};

// Set Token
export const setToken = (token) => {
    cookie.set('token', token, { path: '/' });
};

// Get Current Tenant
export const getCurrentTenant = () => {
    return localStorage.getItem('tenant');
};
// Set Current Tenant
export const setCurrentTenant = (tenant) => {
    localStorage.setItem('tenant', tenant);
};

// Get Token
export const getToken = () => {
    return cookie.get('token');
};
export const getAdminToken = () => {
    return cookie.get('adminToken');
};
export const setAdminToken = (adminToken) => {
    cookie.set('adminToken', adminToken, { path: '/' });
};
export const getDonorEmail = () => {
    return localStorage.getItem('donorEmail');
};
// Set Donor Email
export const setDonorEmail = (donorEmail) => {
    localStorage.setItem('donorEmail', donorEmail);
};

// Get Current Language
export const getCurrentLanguage = () => {
    return localStorage.getItem('selectedLanguage') || 'en';
};

// Set Current Language
export const setCurrentLanguage = (language) => {
    localStorage.setItem('selectedLanguage', language);
};

// Delete Token
export const deleteToken = () => {
    cookie.remove('token', { path: '/' });
    localStorage.clear();
    sessionStorage.clear();
};
export const clearLocalStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
};
export const clearSessionStorage = () => {
    sessionStorage.clear();
};


// Instance From Axios
const axiosInstance = axios.create({
    baseURL: baseURL,
    Accept: 'application/json',
    headers: {
        'tenant': ensureTenantInitialized(),
        "Content-Type": "application/json",
        'Accept-Language': getCurrentLanguage(),
    }
});

// Axios Interceptors Request
axiosInstance.interceptors.request.use(
    async config => {
        const token = getToken();
        const currentLanguage = getCurrentLanguage();
        const currentTenant = getCurrentTenant() || ensureTenantInitialized();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            config.headers.Accept = 'application/json';
            // config.headers["Content-Type"] = 'multipart/form-data'
        }
        // Always set tenant header for all requests (even unauthenticated)
        config.headers.tenant = currentTenant;

        // Always set Accept-Language header for all requests

    
        config.headers['Accept-Language'] = currentLanguage;

        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Axios Interceptors Response
axiosInstance.interceptors.response.use(
    response => {
        // Normalize all URLs in response data to HTTPS to prevent Mixed Content issues
        if (response.data) {
            response.data = normalizeUrlsToHttps(response.data);
        }
        return response;
    },
    async error => {
        const originalRequest = error.config;

        if (error.code === "ERR_NETWORK" && navigateFunction) {
            navigateFunction('/ServerUnavailable');
            return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry && getToken()) {
            originalRequest._retry = true;
            try {
                // Use the correct refresh endpoint based on user type
                const isMasterUser = localStorage.getItem('isAdmin') === 'true';
                const refreshEndpoint = isMasterUser ? 'MasterUser/refresh' : 'User/refresh';
                const response = await axiosInstance.post(refreshEndpoint);
                setToken(response.data.accessToken);
                originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                deleteToken();
                window.dispatchEvent(new Event('authChange'));
                if (navigateFunction) {
                    navigateFunction('/membership');
                }
            }
        } else if (error.response?.status === 460 && navigateFunction) {
            deleteToken();
            localStorage.clear();
            navigateFunction('/login');
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
